/**
 * Designed  & Developed by Faisal Nadaf
 * Contact: faisalfnadaf@gmail.com
 *
 * @format
 */

import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";

type LoadingScreenProps = {
	minDisplayTime?: number;
	onLoadingComplete?: () => void;
};

export default function LoadingScreen({
	minDisplayTime = 3500,
	onLoadingComplete,
}: LoadingScreenProps) {
	const { t } = useTranslation();
	const [isVisible, setIsVisible] = useState(true);
	const [progress, setProgress] = useState(0);

	// Simulated progress (smooth + controlled)
	useEffect(() => {
		let start = Date.now();

		const interval = setInterval(() => {
			const elapsed = Date.now() - start;
			const percentage = Math.min(
				100,
				Math.floor((elapsed / minDisplayTime) * 100),
			);
			setProgress(percentage);
		}, 40);

		const timer = setTimeout(() => {
			setIsVisible(false);
			onLoadingComplete?.();
		}, minDisplayTime);

		return () => {
			clearInterval(interval);
			clearTimeout(timer);
		};
	}, [minDisplayTime, onLoadingComplete]);

	return (
		<AnimatePresence>
			{isVisible && (
				<motion.div
					initial={{ opacity: 0 }}
					animate={{ opacity: 1 }}
					exit={{ opacity: 0, y: -40 }}
					transition={{ duration: 0.6, ease: "easeInOut" }}
					className="
	fixed top-0 left-0
	h-screen w-screen
	z-99999999
	flex items-center justify-center
	overflow-hidden
	bg-[rgb(67,0,139)] 
	
">
					{/* Background Glow */}
					<motion.div
						className="absolute w-[600px] h-[600px] rounded-full blur-3xl opacity-20"
						style={{ background: "#2d2f92" }}
						animate={{
							scale: [1, 1.2, 1],
							opacity: [0.15, 0.25, 0.15],
						}}
						transition={{
							duration: 4,
							repeat: Infinity,
							ease: "easeInOut",
						}}
					/>

					{/* MAIN CONTENT */}
					<div className="relative flex flex-col items-center gap-8">
						{/* LOGO + RINGS */}
						<div className="relative flex items-center justify-center">
							{/* Outer rotating ring */}
							<motion.div
								animate={{ rotate: 360 }}
								transition={{
									duration: 6,
									repeat: Infinity,
									ease: "linear",
								}}
								className="absolute h-40 w-40 rounded-full border border-white/10"
							/>

							{/* Pulse ring */}
							<motion.div
								animate={{
									scale: [1, 1.15, 1],
									opacity: [0.4, 0.1, 0.4],
								}}
								transition={{
									duration: 2,
									repeat: Infinity,
									ease: "easeInOut",
								}}
								className="absolute h-32 w-32 rounded-full border border-[#f9a93a]/40"
							/>

							{/* LOGO */}
							<motion.div
								initial={{ scale: 0.6, opacity: 0, filter: "blur(10px)" }}
								animate={{
									scale: 1,
									opacity: 1,
									filter: "blur(0px)",
								}}
								transition={{
									duration: 1,
									ease: [0.22, 1, 0.36, 1],
								}}
								className="relative z-10 h-28 w-28 flex items-center justify-center rounded-full bg-white/5 backdrop-blur-md shadow-xl">
								<img
									src="/images/logos/Asset8L.png"
									alt={t("common.ui.companyLogo")}
									className="h-full w-full object-contain"
								/>
							</motion.div>
						</div>

						{/* TEXT */}
						<div className="text-center">
							<motion.h1
								initial={{ opacity: 0, y: 10 }}
								animate={{ opacity: 1, y: 0 }}
								transition={{ delay: 0.3 }}
								className="text-4xl font-semibold tracking-tight text-white">
								Positron
							</motion.h1>

							<motion.p
								initial={{ opacity: 0 }}
								animate={{ opacity: 1 }}
								transition={{ delay: 0.5 }}
								className="mt-1 text-xs tracking-[0.35em] text-white/50 uppercase">
								Engineering Corporation{" "}
							</motion.p>
						</div>

						{/* PROGRESS */}
						<div className="w-64">
							{/* BAR */}
							<div className="h-[2px] w-full bg-white/10 overflow-hidden">
								<motion.div
									style={{ width: `${progress}%` }}
									className="h-full bg-gradient-to-r from-[#2d2f92] to-[#f9a93a]"
								/>
							</div>

							{/* TEXT */}
							<div className="mt-2 flex justify-between text-xs text-white/40">
								<span>Loading...</span>
								<span>{progress}%</span>
							</div>
						</div>
					</div>
				</motion.div>
			)}
		</AnimatePresence>
	);
}
